import { DollarSign, TrendingUp, Clock, ArrowDownRight } from 'lucide-react';
import { useAccountStore } from '../../stores/useAccountStore';
import s from './Pages.module.css';

export function EarningsPage() {
  const earnings = useAccountStore(a => a.earnings);
  const sorted = [...earnings].sort((a, b) => b.timestamp - a.timestamp);

  const total = earnings.reduce((sum, e) => sum + parseFloat(e.amount || '0'), 0);
  const pending = earnings.filter(e => e.status === 'pending').reduce((sum, e) => sum + parseFloat(e.amount || '0'), 0);
  const settled = earnings.filter(e => e.status === 'claimed' || e.status === 'settled').reduce((sum, e) => sum + parseFloat(e.amount || '0'), 0);

  const sources = new Map<string, number>();
  earnings.forEach(e => {
    sources.set(e.source, (sources.get(e.source) ?? 0) + parseFloat(e.amount || '0'));
  });

  return (
    <div className={s.page}>
      <div className={s.pageHeader}>
        <h1 className={s.pageTitle}>Earnings</h1>
        <p className={s.pageSubtitle}>Revenue from subscriptions, marketplace sales, and platform rewards.</p>
      </div>

      <div className={s.statsRow}>
        <div className={s.statCard}>
          <div className={s.statLabel}><TrendingUp size={14} /> Total Earned</div>
          <div className={s.statValue}>{total.toLocaleString()} NAT</div>
        </div>
        <div className={s.statCard}>
          <div className={s.statLabel}><Clock size={14} /> Pending</div>
          <div className={s.statValue}>{pending.toLocaleString()} NAT</div>
        </div>
        <div className={s.statCard}>
          <div className={s.statLabel}><ArrowDownRight size={14} /> Settled</div>
          <div className={s.statValue}>{settled.toLocaleString()} NAT</div>
        </div>
        <div className={s.statCard}>
          <div className={s.statLabel}><DollarSign size={14} /> Sources</div>
          <div className={s.statValue}>{sources.size}</div>
        </div>
      </div>

      {/* Source breakdown */}
      {sources.size > 0 && (
        <>
          <div className={s.sectionHeader}>
            <h2 className={s.sectionTitle}>By Source</h2>
          </div>
          <div className={s.cardGrid}>
            {[...sources.entries()].map(([src, amt]) => (
              <div key={src} className={s.card}>
                <div className={s.cardTitle}>{src}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-accent)', marginTop: 8 }}>{amt.toLocaleString()} NAT</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Transactions */}
      <div className={s.sectionHeader}>
        <h2 className={s.sectionTitle}>History</h2>
      </div>

      {sorted.length === 0 ? (
        <div className={s.emptyState}>
          <div className={s.emptyIcon}><DollarSign size={48} /></div>
          <div className={s.emptyTitle}>No earnings yet</div>
          <div className={s.emptyDesc}>Publish prefabs or subscribe to services to start earning.</div>
        </div>
      ) : (
        <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)' }}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Source</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(e => (
                <tr key={e.id}>
                  <td style={{ fontWeight: 500 }}>{e.source}</td>
                  <td style={{ fontWeight: 600, color: 'var(--color-accent)' }}>{e.amount} NAT</td>
                  <td>
                    <span className={`${s.badge} ${e.status === 'claimed' || e.status === 'settled' ? s.badgeSuccess : s.badgeWarning}`}>
                      {e.status}
                    </span>
                  </td>
                  <td style={{ color: 'var(--color-text-tertiary)', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {new Date(e.timestamp).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
